import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ShieldCheck, Loader2, CheckCircle2, XCircle, AlertTriangle } from "lucide-react";

/**
 * Public OAuth-button verification landing page. The bot posts a link button in
 * #verify → it opens this page → user authorizes with Discord → backend assigns
 * the Verified role (removes Unverified) and signs them into the site.
 */
export default function VerifyDiscord() {
  const [params] = useSearchParams();
  const { user, refresh } = useAuth();
  const [available, setAvailable] = useState(null);
  const [starting, setStarting] = useState(false);
  const status = params.get("verify"); // success | partial | role_failed | null

  useEffect(() => {
    api.get("/config/verify").then((r) => setAvailable(!!r.data.available)).catch(() => setAvailable(false));
    if (status) refresh?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const start = async () => {
    setStarting(true);
    try {
      const { data } = await api.get("/auth/discord/verify-start");
      window.location.href = data.url;
    } catch {
      setStarting(false);
    }
  };

  const Shell = ({ children }) => (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-6 py-16 relative grain" data-testid="verify-discord-page">
      <div className="w-full max-w-md surface-card p-8 text-center">{children}</div>
    </div>
  );

  if (status === "success" || status === "partial") {
    return (
      <Shell>
        <div data-testid="verify-discord-success">
          <div className="mx-auto w-14 h-14 rounded-full bg-emerald-500/15 flex items-center justify-center mb-5">
            <CheckCircle2 className="text-emerald-400" size={30} />
          </div>
          <div className="label-eyebrow text-emerald-400 mb-2">▮ VERIFIED</div>
          <h1 className="font-display text-3xl mb-3">You&apos;re verified{user?.name ? `, ${user.name}` : ""}!</h1>
          <p className="text-[var(--ff-text-dim)] text-sm mb-6">
            The <b className="text-white">Verified</b> role has been added to your Discord account and you now have full
            access to the server. You can head back to Discord.
          </p>
          {status === "partial" && (
            <div className="text-amber-400 text-xs font-mono-ui border border-amber-400/30 rounded-md px-3 py-2 mb-5">
              Some role changes didn&apos;t fully apply — if you don&apos;t see the Verified role in a minute, contact a mod.
            </div>
          )}
          <Link to="/" className="btn-primary w-full justify-center text-sm" data-testid="verify-discord-home-btn">Go to Homepage</Link>
        </div>
      </Shell>
    );
  }

  if (status === "role_failed") {
    return (
      <Shell>
        <div data-testid="verify-discord-error">
          <div className="mx-auto w-14 h-14 rounded-full bg-[var(--ff-red)]/15 flex items-center justify-center mb-5">
            <XCircle className="text-[var(--ff-red)]" size={30} />
          </div>
          <div className="label-eyebrow text-[var(--ff-red)] mb-2">▮ ALMOST THERE</div>
          <h1 className="font-display text-3xl mb-3">Couldn&apos;t assign your role</h1>
          <p className="text-[var(--ff-text-dim)] text-sm mb-6">
            You authorized successfully, but the bot couldn&apos;t assign the Verified role. This usually means the bot
            needs a higher role position or Manage&nbsp;Roles permission. Please try again or ping a mod.
          </p>
          <button onClick={start} disabled={starting} className="btn-primary w-full justify-center text-sm" data-testid="verify-discord-retry-btn">
            {starting ? <Loader2 size={15} className="animate-spin" /> : <ShieldCheck size={15} />} Try Again
          </button>
        </div>
      </Shell>
    );
  }

  if (available === false) {
    return (
      <Shell>
        <div data-testid="verify-discord-unavailable">
          <div className="mx-auto w-14 h-14 rounded-full bg-amber-500/15 flex items-center justify-center mb-5">
            <AlertTriangle className="text-amber-400" size={28} />
          </div>
          <div className="label-eyebrow text-amber-400 mb-2">▮ NOT SET UP YET</div>
          <h1 className="font-display text-3xl mb-3">Verification isn&apos;t available</h1>
          <p className="text-[var(--ff-text-dim)] text-sm">
            An admin still needs to finish setting up Discord verification. Please check back soon.
          </p>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="mx-auto w-14 h-14 rounded-full bg-[var(--ff-red)]/15 flex items-center justify-center mb-5">
        <ShieldCheck className="text-[var(--ff-red)]" size={30} />
      </div>
      <div className="label-eyebrow text-[var(--ff-red)] mb-2">▮ SERVER VERIFICATION</div>
      <h1 className="font-display text-3xl mb-3">Verify your account</h1>
      <p className="text-[var(--ff-text-dim)] text-sm mb-7">
        Authorize with Discord and we&apos;ll instantly give you the <b className="text-white">Verified</b> role and unlock
        the full server. It takes one click — no forms, no codes.
      </p>
      <button
        onClick={start}
        disabled={starting || available === null}
        className="btn-primary w-full justify-center text-sm"
        data-testid="verify-discord-authorize-btn"
      >
        {starting || available === null
          ? <Loader2 size={16} className="animate-spin" />
          : <ShieldCheck size={16} />}
        {starting ? "Redirecting to Discord…" : "Authorize with Discord"}
      </button>
      <p className="text-[0.65rem] font-mono-ui text-[var(--ff-text-dim)] uppercase tracking-[0.15em] mt-4">
        We only read your Discord identity to assign your role.
      </p>
    </Shell>
  );
}
